/* tu-dep-media.js — the small photo/video slider on a department band's media
   side (sections/tu-departments.liquid, 2026-09-11).

   Deliberately not part of assets/tu-video.js. That file drives the Target
   Reviews carousel: a looping rail with cloned slots, focus autoplay, a turn
   timer, YouTube players and a sound control. This is a two- or three-slide
   fade with two arrows and no timer. Sharing the file would mean carrying all
   of that machinery for a component that wants none of it.

   Rotation is AUTOMATIC but starts only once the slider is actually on screen
   (2026-09-11, client). That gate is load-bearing, not caution: the slides are
   two 6-7 MB clips with preload="none", and rotating from the moment the page
   parses would fetch both of them on every homepage visit whether or not the
   visitor ever scrolled this far. An IntersectionObserver keeps the earlier
   win -- zero video bytes on load -- while still giving the band the movement
   that was asked for.

   Pauses on hover, on a hidden tab, and under prefers-reduced-motion, which
   also disables rotation entirely.

   Sound (2026-09-11, client: "the audio play is not working ... on fishing
   section"): the clips used to carry a hardcoded `muted` attribute and no
   control, so there was no route to sound at all. They start muted -- rotation
   is unasked-for playback and nobody wants a homepage that makes a noise -- and
   a Sound button unmutes the clip on screen. The choice is stored under the
   same sessionStorage key assets/tu-video.js uses, so turning sound on here or
   on a review carries across the visit. */
(function () {
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Same key, same contract as assets/tu-video.js: '1'/'0', and a private
  // window can make sessionStorage throw on access rather than return null.
  var SOUND_KEY = 'tu:sound';
  function soundRead() {
    try { var v = sessionStorage.getItem(SOUND_KEY); return v === null ? null : v === '1'; }
    catch (e) { return null; }
  }
  function soundWrite(on) { try { sessionStorage.setItem(SOUND_KEY, on ? '1' : '0'); } catch (e) {} }

  function bind(fig) {
    if (fig.tuDepMediaBound) return;
    fig.tuDepMediaBound = true;

    var slides = [].slice.call(fig.querySelectorAll('[data-tu-depmedia-slide]'));
    var n = slides.length;
    if (n < 2) return;

    var prev = fig.querySelector('[data-tu-depmedia-prev]');
    var next = fig.querySelector('[data-tu-depmedia-next]');
    var soundBtn = fig.querySelector('[data-tu-depmedia-sound]');
    var wantSound = soundRead() === true;
    var cur = 0;

    function syncSound() {
      if (!soundBtn) return;
      soundBtn.setAttribute('aria-pressed', wantSound ? 'true' : 'false');
      soundBtn.setAttribute('aria-label', wantSound ? 'Turn sound off' : 'Turn sound on');
      soundBtn.classList.toggle('is-on', wantSound);
      var t = soundBtn.querySelector('.tu-trv__sound-txt');
      if (t) t.textContent = wantSound ? 'Sound on' : 'Sound off';
    }

    // Play with the sound the visitor asked for, and fall back to muted if the
    // browser refuses -- an unmuted play() is only allowed after a gesture, and
    // a stored preference from earlier in the visit is not one on a fresh load.
    // Dropping the flag on refusal keeps the button honest rather than leaving
    // it claiming sound is on over a silent clip.
    function playWithSound(v) {
      if (reduced) return;
      v.muted = !wantSound;
      var p = v.play();
      if (!p || !p.catch) return;
      p.catch(function () {
        v.muted = true;
        if (wantSound) { wantSound = false; soundWrite(false); syncSound(); }
        var again = v.play();
        if (again && again.catch) again.catch(function () {});
      });
    }

    function show(i) {
      i = ((i % n) + n) % n;
      cur = i;
      slides.forEach(function (el, j) {
        var on = j === i;
        el.classList.toggle('is-active', on);
        if (on) { el.removeAttribute('aria-hidden'); } else { el.setAttribute('aria-hidden', 'true'); }

        var v = el.querySelector('[data-tu-depmedia-video]');
        if (!v) return;
        if (on && v.hasAttribute('data-tu-poster')) { v.poster = v.getAttribute('data-tu-poster'); v.removeAttribute('data-tu-poster'); }   // 2026-10-06: loader has not reached this band yet
        if (on) {
          // Attach the source lazily. preload="none" keeps the file unfetched
          // until this point; calling play() is what starts the download.
          playWithSound(v);
        } else {
          v.pause();
          v.muted = true;
        }
      });

      // The control belongs to the clip, not to the slider: on the still
      // photograph there is nothing for it to do.
      if (soundBtn) soundBtn.hidden = !slides[i].querySelector('[data-tu-depmedia-video]');
    }

    // --- rotation -------------------------------------------------------
    // Each slide holds for its own dwell: a still photograph needs only a
    // glance, a clip deserves long enough to read as motion rather than a
    // flicker.
    var PHOTO_MS = 5000, VIDEO_MS = 9000;
    var timer = null, hover = false, seen = false;

    function dwell() {
      var el = slides[cur];
      return el && el.querySelector('[data-tu-depmedia-video]') ? VIDEO_MS : PHOTO_MS;
    }
    function stop() { if (timer) { clearTimeout(timer); timer = null; } }
    function schedule() {
      stop();
      if (reduced || !seen || hover || document.hidden) return;
      timer = setTimeout(function () { show(cur + 1); schedule(); }, dwell());
    }

    function step(d) { show(cur + d); schedule(); }   // a press restarts the dwell
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    // Arrow keys when the slider itself has focus, for parity with the arrows.
    fig.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });

    fig.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') { hover = true; stop(); }
    });
    fig.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') { hover = false; schedule(); }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { schedule(); }
    });

    if (soundBtn) {
      syncSound();
      soundBtn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        wantSound = !wantSound;
        soundWrite(wantSound);
        syncSound();
        var v = slides[cur].querySelector('[data-tu-depmedia-video]');
        // This click IS the gesture the browser wanted, so an unmuted play()
        // here is allowed where the same call during rotation was not.
        if (v) { v.muted = !wantSound; var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
      });
    }

    show(0);

    // Nothing rotates, and therefore nothing downloads, until the band is seen.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { seen = true; schedule(); }
          else { stop(); }
        });
      }, { threshold: 0.4 });
      io.observe(fig);
    } else {
      seen = true; schedule();
    }
  }

  function init() {
    document.querySelectorAll('[data-tu-depmedia]').forEach(bind);
  }
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();
